import Cookies from "js-cookie";
import { client } from "@/client/client.gen";

const AUTH_TOKEN_KEY = "auth_tokens";
const REMEMBER_ME_KEY = "auth_remember_me";

export interface TokensInfo {
  token: string;
  refreshToken: string;
  tokenExpires: number;
}

type TokenChangeListener = (tokens: TokensInfo | null) => void;
const listeners: Set<TokenChangeListener> = new Set();

/**
 * Persist the user's "Remember me" preference. Tokens stay in cookies; only
 * the boolean flag lives in localStorage so cookie-write code can compute the
 * correct expiry window without re-querying the form.
 */
export function setRememberMe(value: boolean): void {
  try {
    localStorage.setItem(REMEMBER_ME_KEY, String(value));
  } catch {
    /* localStorage may be unavailable (private mode, quota) — fail closed */
  }
}

/**
 * Read the user's "Remember me" preference. Defaults to false when absent or
 * when localStorage throws (which counts as a non-persistent session).
 */
export function getRememberMe(): boolean {
  try {
    return localStorage.getItem(REMEMBER_ME_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Get current tokens from cookie storage
 */
export function getTokensInfo(): TokensInfo | null {
  try {
    const data = Cookies.get(AUTH_TOKEN_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data);

    // Validate structure
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.token === "string" &&
      typeof parsed.refreshToken === "string" &&
      typeof parsed.tokenExpires === "number"
    ) {
      return parsed as TokensInfo;
    }

    // Corrupted cookie: clear it
    setTokensInfo(null);
    return null;
  } catch {
    // Parsing failed: clear corrupted cookie
    setTokensInfo(null);
    return null;
  }
}

/**
 * Resolve the cookie `domain` attribute for the current environment.
 *
 * Returns `undefined` (host-only cookie) for:
 *   - dev / vite preview / any non-prod build
 *   - any non-heycircle.com hostname (preview deploys served on
 *     `*.vercel.app`, custom domains, localhost, ad-hoc test hosts)
 * Returns `.heycircle.com` (cross-subdomain) when the current host actually
 * lives under `heycircle.com`, or whatever `VITE_COOKIE_DOMAIN` overrides to.
 *
 * Setting `domain=.heycircle.com` on a host that isn't `*.heycircle.com`
 * makes the browser reject the cookie write silently → users appear logged
 * out on next reload. Hostname gating is the only safe default; the env
 * override exists so deploys with a custom domain (e.g. white-labelled
 * tenants) can opt in explicitly.
 */
function resolveCookieDomain(): string | undefined {
  if (import.meta.env.DEV) return undefined;

  const override = import.meta.env.VITE_COOKIE_DOMAIN as string | undefined;
  if (override) return override;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "heycircle.com" || host.endsWith(".heycircle.com")) {
    return ".heycircle.com";
  }
  return undefined;
}

/**
 * Set tokens in cookie storage and update API client headers.
 *
 * Cookie attributes:
 *   - expires:  30 days when rememberMe=true, 1 day otherwise
 *   - domain:   `.heycircle.com` only when the current host actually lives
 *               under heycircle.com (or `VITE_COOKIE_DOMAIN` overrides);
 *               otherwise host-only so the cookie write doesn't get rejected
 *   - secure:   true outside dev (HTTPS-only)
 *   - sameSite: lax in all envs
 *
 * Does NOT clear the rememberMe preference. Use `removeTokensInfo()` for
 * explicit logout — `setTokensInfo(null)` is also called from non-logout
 * paths (corrupted-cookie cleanup, refresh-token rejection) and the user's
 * preference must survive those.
 *
 * Notifies all listeners synchronously to prevent state gaps.
 */
export function setTokensInfo(tokens: TokensInfo | null, notifyListeners: boolean = true): void {
  if (tokens) {
    const rememberMe = getRememberMe();
    const expires = rememberMe ? 30 : 1;
    const domain = resolveCookieDomain();

    const options: Cookies.CookieAttributes = {
      expires,
      path: "/",
      sameSite: "lax",
    };

    if (domain) {
      options.domain = domain;
      options.secure = true;
    } else if (!import.meta.env.DEV) {
      // HTTPS-only outside dev even when host-only.
      options.secure = true;
    }

    Cookies.set(AUTH_TOKEN_KEY, JSON.stringify(tokens), options);

    // Update API client headers immediately
    client.setConfig({
      headers: {
        Authorization: `Bearer ${tokens.token}`,
      },
    });
  } else {
    // Clear cookies and headers (must match path used when setting).
    // Try both host-only and parent-domain (when applicable) so we clean
    // up after env switches or hostname changes.
    Cookies.remove(AUTH_TOKEN_KEY, { path: "/" });
    const domain = resolveCookieDomain();
    if (domain) {
      Cookies.remove(AUTH_TOKEN_KEY, { path: "/", domain });
    }
    client.setConfig({
      headers: {
        Authorization: undefined,
      },
    });
  }

  // Notify listeners synchronously
  if (notifyListeners) {
    notifyTokenChange(tokens);
  }
}

/**
 * Explicit-logout helper: clears tokens AND drops the rememberMe preference
 * so the next sign-in starts from a clean slate (D-03: rememberMe must NOT
 * survive an explicit logout). Use this from the auth provider's logOut
 * flow only — for parse errors / refresh failures, call `setTokensInfo(null)`
 * directly so the user's preference is preserved.
 */
export function removeTokensInfo(notifyListeners: boolean = true): void {
  setTokensInfo(null, notifyListeners);
  try {
    localStorage.removeItem(REMEMBER_ME_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Subscribe to token changes
 */
export function subscribeToTokenChanges(listener: TokenChangeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Notify all listeners of token change
 */
function notifyTokenChange(tokens: TokensInfo | null): void {
  listeners.forEach((listener) => {
    try {
      listener(tokens);
    } catch (error) {
      console.error("Token change listener error:", error);
    }
  });
}
